/* eslint-disable lines-around-comment */
/* eslint-disable camelcase */

/**
 * @typedef {Object} MivaDate
 * @prop {number} time_t
 * @prop {number} year
 * @prop {number} month
 * @prop {number} day
 * @prop {number} hour
 * @prop {number} minute
 * @prop {number} second
 * @prop {number} timezone
 */

/**
 * @typedef {Object} MivaPage
 * @prop {number} id
 * @prop {string} code
 * @prop {string} title
 */

/**
 * @typedef {Object} MivaCategory
 * @prop {number} id
 * @prop {string} code
 * @prop {string} name
 * @prop {string} url
 */

/**
 * @typedef {Object} MivaDiscount
 * @prop {string} descrip
 * @prop {number} discount
 * @prop {string} formatted_discount
 */

/**
 * @typedef {Object} MivaImageSizes
 * @prop {string} url
 * @prop {number} height
 * @prop {number} width
 */

/**
 * @typedef {Object} MivaImage
 * @prop {string} code
 * @prop {string} descrip
 * @prop {MivaImageSizes[]} sizes
 */

/**
 * @typedef {Object<string, MivaImageType>} MivaImageTypes
 */

/**
 * @typedef {Object} MivaImageType
 * @prop {string} descrip
 * @prop {MivaImageSizes[]} sizes
 */

/**
 * @typedef {Object} MivaAttributeOption
 * @prop {number} attr_id
 * @prop {number} disp_order
 * @prop {number} id
 * @prop {number} price
 * @prop {number} product_id
 * @prop {number} weight
 * @prop {string} code
 * @prop {string} formatted_price
 * @prop {string} prompt
 */

/**
 * @typedef {Object} MivaAttribute
 * @prop {number} attemp_id
 * @prop {string} code
 * @prop {number} default_id
 * @prop {number} disp_order
 * @prop {string} formatted_price
 * @prop {number} id
 * @prop {string} image
 * @prop {boolean} inventory
 * @prop {MivaAttributeOption[]} options
 * @prop {number} price
 * @prop {number} product_id
 * @prop {string} prompt
 * @prop {boolean} required
 * @prop {string} type
 * @prop {number} weight
 */

/**
 * @typedef {Object} MivaProduct
 * @prop {MivaAttribute} attributes
 * @prop {MivaCategory} category
 * @prop {any} CustomField_Values
 * @prop {MivaDiscount[]} discounts
 * @prop {MivaDate} dt_created
 * @prop {MivaDate} dt_updated
 * @prop {MivaImage[]} images
 * @prop {MivaImageTypes} imagetypes
 * @prop {MivaPage} page
 * @prop {boolean} inv_active
 * @prop {boolean} taxable
 * @prop {number} base_price
 * @prop {number} disp_order
 * @prop {number} id
 * @prop {number} inv_available
 * @prop {number} inv_instock
 * @prop {number} price
 * @prop {number} quantity
 * @prop {number} retail
 * @prop {number} sale_price
 * @prop {number} weight
 * @prop {number} output_disp_order
 * @prop {string} code
 * @prop {string} descrip
 * @prop {string} formatted_base_price
 * @prop {string} formatted_price
 * @prop {string} formatted_retail
 * @prop {string} image
 * @prop {string} inv_level
 * @prop {string} inv_long
 * @prop {string} inv_short
 * @prop {string} name
 * @prop {string} sku
 * @prop {string} thumbnail
 * @prop {string} url
 */

/**
 * @typedef {Object} MivaJsonApiFilterValue
 * @prop {string} field
 * @prop {'CO'|'EQ'|'FALSE'|'GE'|'GT'|'IN'|'LE'|'LIKE'|'LT'|'NC'|'NE'|'NOT_IN'|'NOTLIKE'|'NULL'|'TRUE'} operator
 * @prop {any} value
 */

/**
 * @typedef {Object} MivaJsonApiFilter
 * @prop {string} name
 * @prop {MivaJsonApiFilterValue[]} value
 */

/**
 * @typedef {Object} MivaJsonApiResponse
 * @prop {0|1} success
 * @prop {string} [error_code]
 * @prop {string} [error_message]
 * @prop {any} [data]
 */

/**
 * @typedef {Object} CategoryProductListControllerResponse
 * @prop {number} total_count
 * @prop {number} start_offset
 * @prop {MivaProduct[]} data
 */

export const CPLC_DEFAULT_OPTIONS = {
	categoryCode: '',
	categoryId: '',
	count: 5,
	endpoint: window.mivaJS.JSON_Url,
	/**
	 * @type {MivaJsonApiFilter[]}
	 */
	filters: [],
	offset: 0,
	onDemandColumns: [],
	sessionId: window.mivaJS.Session_ID,
	sort: 'disp_order',
	storeCode: window.mivaJS.Store_Code
};

export class CategoryProductListController {

	/**
	 * @type {import('lit').ReactiveControllerHost}
	 */
	host;

	/**
	 * @type {MivaProduct[]}
	 */
	products = [];

	/**
	 * @type {CategoryProductListControllerResponse}
	 */
	response;

	/**
	 * @type {boolean}
	 */
	loaded = false;

	constructor (host, options = CPLC_DEFAULT_OPTIONS) {
		// Add controller to host
		(this.host = host).addController(this);

		// Set options
		this.categoryCode = options.categoryCode ?? CPLC_DEFAULT_OPTIONS.categoryCode;
		this.categoryId = options.categoryId ?? CPLC_DEFAULT_OPTIONS.categoryId;
		this.count = options.count ?? CPLC_DEFAULT_OPTIONS.count;
		this.endpoint = options.endpoint ?? CPLC_DEFAULT_OPTIONS.endpoint;
		this.filters = options.filters ?? CPLC_DEFAULT_OPTIONS.filters;
		this.offset = options.offset ?? CPLC_DEFAULT_OPTIONS.offset;
		this.onDemandColumns = options.onDemandColumns ?? CPLC_DEFAULT_OPTIONS.onDemandColumns;
		this.sessionId = options.sessionId ?? CPLC_DEFAULT_OPTIONS.sessionId;
		this.sort = options.sort ?? CPLC_DEFAULT_OPTIONS.sort;
		this.storeCode = options.storeCode ?? CPLC_DEFAULT_OPTIONS.storeCode;
	}

	get body () {
		return JSON.stringify({
			...this.categoryCode
				? {
					category_code: this.categoryCode
				}
				: {},
			...this.categoryId
				? {
					category_id: this.categoryId
				}
				: {},
			count: this.count,
			filter: [
				...this.filters,
				{
					name: 'ondemandcolumns',
					value: this.onDemandColumns
				}
			],
			function: 'Runtime_CategoryProductList_Load_Query',
			offset: this.offset,
			session_id: this.sessionId,
			session_type: 'runtime',
			sort: this.sort,
			store_code: this.storeCode
		});
	}

	/**
	 * Send the Runtime API call and assign this.products
	 * @async
	 */
	async getProducts () {
		// Set loaded to false
		this.loaded = false;

		// Get the response
		const response = await fetch(this.endpoint, {
			body: this.body,
			headers: {
				'Content-Type': 'application/json'
			},
			method: 'POST'
		});

		/**
		 * @type {MivaJsonApiResponse}
		 */
		const mivaResponse = await response.json();

		// Handle response success or error
		if (mivaResponse.success) {
			// Set response
			this.response = mivaResponse.data;

			// Set products array
			this.products = this.response.data;
		}
		else {
			// eslint-disable-next-line no-console
			console.error(this.constructor.name, '::', `${mivaResponse.error_code}: ${mivaResponse.error_message}`);

			// Unset products array and response
			this.products = [];
			this.response = {};
		}

		// Set loaded to true
		this.loaded = true;

		// Request an update to the host
		this.host.requestUpdate();

		// Return the product list
		return this.products;
	}
};
